import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { Footer } from './Footer';

import './css/RequestDemo.css'

import { HeroShader, DARK_PALETTE } from './HeroShader';
import { Logo3D } from './Logo3D';
import { fadeEngine } from './Fade';

import { TRIAL_PLAN_PARAM } from './contact'

const FORMSPREE_FORM_ID: string = 'mojopkqb'

///@brief   Subject line of the email Formspree sends for a request; the plan
///         clicked on the way here, when there was one, goes after it.
const SUBJECT: string = 'Trial request'

// The flat picture of the mark, for browsers without WebGL.
const logoSrc: string = require( './images/logos/Logo.002.Dark.png' )

// What the trial is, in three words each, under the form.
const POINTS: string[] = [
	'Free',
	'On your own infrastructure',
	'Set up with you',
]


function Check(): React.JSX.Element {
	return (
		<svg className="RequestDemo-Check" viewBox="0 0 22 22" fill="none" aria-hidden="true" >
			<circle cx="11" cy="11" r="9.6" />
			<path d="M6.8 11.3 L9.7 14.1 L15.2 7.9" />
		</svg>
	)
}


// The trial request: the hero's shader and its mark in three dimensions,
// the form under them, on a page of its own.
export function RequestDemo(): React.JSX.Element {

	const [ email, setEmail ] = React.useState<string>( '' )
	const [ status, setStatus ] = React.useState<'idle' | 'submitting' | 'success' | 'error'>( 'idle' )

	// The plan the visitor clicked on the way here, if they came from a
	// pricing card: shown under the form, and sent with the email.
	const [ searchParams ] = useSearchParams()
	const plan: string | null = searchParams.get( TRIAL_PLAN_PARAM )
	const subject: string = plan === null ? SUBJECT : `${ SUBJECT } — ${ plan }`

	// This page carries its own shell rather than the Page one, so the tab's
	// title is set here, as Page does for every other page.
	React.useEffect( (): ( () => void ) => {
		const previousTitle: string = document.title
		document.title = 'Underlayer — Try it'

		fadeEngine.useEffect()

		return (): void => {
			document.title = previousTitle
		}
	}, [] );

	const onSubmit = async ( e: React.FormEvent<HTMLFormElement> ): Promise<void> => {
		e.preventDefault()

		setStatus( 'submitting' )

		try {
			const response: Response = await fetch( `https://formspree.io/f/${ FORMSPREE_FORM_ID }`, {
				method: 'POST',
				headers: { 'Accept': 'application/json' },
				body: new FormData( e.currentTarget ),
			} )

			setStatus( response.ok ? 'success' : 'error' )
		} catch {
			setStatus( 'error' )
		}
	}

	return (
		<>
			<div className="Pane-Light RequestDemo" >
				<HeroShader { ...DARK_PALETTE } />
				{ /* Clears the drifting light from behind the words, as on the
				     hero: the pattern sinks back into black around the text. */ }
				<div className="RequestDemo-Scrim" aria-hidden="true" />

				<div className="RequestDemo-Container" >

					{ /* The first thing on the page: the menu bar stays clear until
					     it rises to meet the bar (Menu.tsx). */ }
					<div className="RequestDemo-Logo" data-menu-veil >
						<Logo3D fallbackSrc={ logoSrc } />
					</div>

					<div className="Page-Label" data-fade >Free trial</div>
					<div className="Title text-shadow-2" data-fade data-fade-delay="0.05" >Try underlayer on your own servers.</div>

					<div className="RequestDemo-Text fs-18" data-fade data-fade-delay="0.2" >
						Leave us your email. We get back to you shortly and set the container up with
						you, on your infrastructure.
					</div>

					{ status === 'success' ? (
						<div className="RequestDemo-Success" >
							<Check />
							<span>Thanks! We will be in touch shortly.</span>
						</div>
					) : (
						<form className="RequestDemo-Form" data-fade data-fade-delay="0.3" onSubmit={ onSubmit } >
							<input type="hidden" name="_subject" value={ subject } />
							{ plan !== null && <input type="hidden" name="plan" value={ plan } /> }
							<input
								type="email"
								name="email"
								required
								placeholder="you@company.com"
								aria-label="Your email"
								value={ email }
								onChange={ ( e: React.ChangeEvent<HTMLInputElement> ): void => setEmail( e.target.value ) }
								className="RequestDemo-Input"
							/>
							<button type="submit" className="RequestDemo-Submit" disabled={ status === 'submitting' } >
								{ status === 'submitting' ? 'Sending…' : 'Request a trial' }
							</button>
						</form>
					) }

					{ status === 'error' && (
						<div className="RequestDemo-Error fs-16" >Something went wrong, please try again.</div>
					) }

					{ plan !== null && status !== 'success' && (
						<div className="RequestDemo-Plan fs-16" data-fade data-fade-delay="0.35" >
							<span className="RequestDemo-Plan-Label" >Plan</span>
							<span>{ plan }</span>
						</div>
					) }

					<ul className="RequestDemo-Points" data-fade data-fade-delay="0.4" >
						{ POINTS.map( ( point: string ): React.JSX.Element => (
							<li className="RequestDemo-Point" key={ point } >
								<Check />
								{ point }
							</li>
						) ) }
					</ul>

				</div>
			</div>
			<Footer />
		</>
	);

}
